import { useEffect, useState, useMemo } from "react";
import {
  Menu, Bell, Search, ExternalLink, Moon, Sun, Lock,
  BookOpen, Zap, Calculator, Trophy, HelpCircle,
  CreditCard, Shield, Users, ArrowRight, X, Clock, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useTheme } from "@/App";

const Header = ({ onMenuClick, title }) => {
  const { user, logoutUrl } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { theme, setTheme } = useTheme();
  const isLight = theme === 'light';

  // Search Palette state (M1)
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const searchableItems = useMemo(() => {
    const items = [
      { id: "courses", title: "Courses & Masterclasses", desc: "Browse curriculum and enrolled courses", path: "/courses", icon: BookOpen, category: "Learning" },
      { id: "drishti", title: "Drishti AI Signals", desc: "Live institutional trade setups and alerts", path: "/drishti", icon: Zap, category: "Trading" },
      { id: "calculator", title: "Risk & Position Calculator", desc: "Quantified sizing, risk-to-reward & stop-loss", path: "/calculator", icon: Calculator, category: "Tools" },
      { id: "journal", title: "Trade Journal & Analytics", desc: "Log trades, track win rate, review performance", path: "/journal", icon: FileText, category: "Trading" },
      { id: "leaderboard", title: "Community Leaderboard", desc: "XP levels, trader rankings & achievement badges", path: "/leaderboard", icon: Trophy, category: "Community" },
      { id: "referrals", title: "Referral Rewards", desc: "Invite friends, track rewards and coin balance", path: "/referrals", icon: Users, category: "Rewards" },
      { id: "support", title: "Support & FAQs", desc: "Get help, submit tickets, view common answers", path: "/support", icon: HelpCircle, category: "Help" },
      { id: "profile", title: "Profile & Settings", desc: "Personal info, Dhan credentials, password", path: "/profile", icon: Shield, category: "Account" },
      { id: "payments", title: "Payment History", desc: "View invoices, subscriptions, and receipts", path: "/payments", icon: CreditCard, category: "Account" }
    ];

    if (isAdmin) {
      items.unshift(
        { id: "admin-analytics", title: "Admin Analytics", desc: "Platform overview, revenue, student metrics", path: "/admin", icon: Shield, category: "Admin" },
        { id: "admin-alerts", title: "Drishti Alerts Manager", desc: "Approve, edit, or reject live institutional signals", path: "/admin/alerts", icon: Zap, category: "Admin" },
        { id: "admin-drishti", title: "Drishti Access Controller", desc: "Manage Pro access, subscriptions & validity", path: "/admin?tab=drishti-requests", icon: Lock, category: "Admin" },
        { id: "admin-team", title: "Team & Biometric Attendance", desc: "Manage mentors, attendance punches, and tasks", path: "/admin/team", icon: Users, category: "Admin" },
        { id: "admin-users", title: "User Management", desc: "Manage students, counselors, roles & credentials", path: "/admin/users", icon: Users, category: "Admin" },
        { id: "admin-leads", title: "Contact Leads & Inquiries", desc: "View incoming student inquiries and callbacks", path: "/admin/leads", icon: Clock, category: "Admin" }
      );
    }

    return items;
  }, [isAdmin]);

  const filteredSearchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return searchableItems;
    return searchableItems.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.desc.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q)
    );
  }, [searchableItems, searchQuery]);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSelectResult = (path) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(path);
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Notifications error:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 30000);
    return () => clearInterval(iv);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNotifications = async (open) => {
    if (!open || unreadCount === 0) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({
        ...n,
        read_by: Array.from(new Set([...(n.read_by || []), user?.user_id].filter(Boolean)))
      })));
    } catch (err) {
      console.error("Mark notifications read error:", err);
    }
  };

  return (
    <header className={`sticky top-0 z-30 backdrop-blur-xl border-b px-4 md:px-6 py-3 transition-colors duration-500 ${isLight ? 'bg-white/80 border-gray-200' : 'bg-[#050505]/80 border-white/[0.06]'}`} data-testid="header">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="icon" className={`md:hidden ${isLight ? 'text-gray-500 hover:text-gray-900' : 'text-[#A1A1AA] hover:text-white'}`} onClick={onMenuClick} data-testid="header-menu-btn">
            <Menu className="w-6 h-6" />
          </Button>
          {title && <h1 className={`text-base sm:text-lg md:text-xl font-semibold font-outfit tracking-tight truncate ${isLight ? 'text-gray-900' : 'text-white'}`}>{title}</h1>}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Interactive Search Box (M1) */}
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-plex transition-all ${
                isLight
                  ? 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-600'
                  : 'bg-white/[0.04] hover:bg-white/[0.08] border-white/[0.08] text-gray-400'
              }`}
              data-testid="header-search-btn"
              title="Search Jamadhan (Cmd+K)"
            >
              <Search className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Search...</span>
              <kbd className="hidden md:inline-block px-1.5 py-0.5 text-[10px] font-mono rounded bg-white/10 text-gray-400 border border-white/10">⌘K</kbd>
            </button>
          </div>

          <button 
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} 
            className={`p-2.5 rounded-full transition-colors hidden sm:flex items-center justify-center ${isLight ? 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200' : 'bg-gray-100 dark:bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'}`}
            title="Toggle Theme"
          >
            {isLight ? <Moon className="w-4 h-4 text-slate-800" /> : <Sun className="w-4 h-4 text-amber-400" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={`hidden sm:flex items-center gap-2 font-medium ${isLight ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100' : 'text-[#A1A1AA] hover:text-white hover:bg-gray-50 dark:bg-white/[0.03]'}`} data-testid="header-community-btn">
                <span className="text-xs font-plex">Community</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={`border ${isLight ? 'bg-white border-gray-200 text-gray-900' : 'bg-[#0C0C10] border-white/[0.06] text-white'}`}>
              <DropdownMenuItem className={`cursor-pointer text-sm ${isLight ? 'hover:bg-gray-100' : 'hover:bg-gray-50 dark:bg-white/[0.03]'}`} onClick={() => window.open("https://t.me/+QOePMys7V481NmZl", "_blank")} data-testid="community-telegram">
                <span className="text-[#0088cc] mr-2 text-xs font-bold">TG</span> Telegram Channel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications Dropdown (H1 - Locked Teasers & Direct Navigation) */}
          <DropdownMenu onOpenChange={openNotifications}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className={`relative ${isLight ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100' : 'text-[#A1A1AA] hover:text-white hover:bg-gray-50 dark:bg-white/[0.03]'}`} data-testid="header-notifications-btn">
                <Bell className="w-[18px] h-[18px]" strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span className={`absolute top-1 right-1 min-w-4 h-4 px-1 text-[10px] leading-4 font-bold rounded-full ${isLight ? 'bg-rose-500 text-white shadow-xs' : 'bg-emerald-600 text-white shadow-xs'}`}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={`w-[calc(100vw-1rem)] max-w-88 border ${isLight ? 'bg-white border-gray-200 text-gray-900 shadow-xl' : 'bg-[#0C0C10] border-white/[0.06] text-white'}`}>
              <DropdownMenuLabel className="font-outfit text-sm flex items-center justify-between">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-semibold">
                    {unreadCount} new
                  </span>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className={isLight ? 'bg-gray-100' : 'bg-white/[0.06]'} />
              {notifications.length === 0 ? (
                <div className={`px-3 py-6 text-center text-xs font-plex ${isLight ? 'text-gray-500' : 'text-[#94A3B8]'}`}>No notifications yet.</div>
              ) : (
                notifications.slice(0, 8).map(item => {
                  const isDrishti = item.type === "drishti_alert" || item.is_locked;
                  const handleClick = () => {
                    if (item.target_url || item.link) {
                      navigate(item.target_url || item.link);
                    } else if (isDrishti) {
                      navigate("/drishti");
                    }
                  };

                  return (
                    <DropdownMenuItem
                      key={item.notification_id}
                      onClick={handleClick}
                      className={`block cursor-pointer py-2.5 px-3 rounded-lg transition-colors ${
                        item.is_locked
                          ? isLight ? 'bg-amber-50/40 hover:bg-amber-50' : 'bg-amber-950/10 hover:bg-amber-950/20'
                          : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.04]'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                          item.read_by?.includes(user?.user_id) ? "bg-slate-400 dark:bg-[#52525B]" : "bg-emerald-500"
                        }`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className={`text-xs font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}>
                              {item.title || "Notification"}
                            </p>
                            {item.is_locked && (
                              <span className="text-[9px] font-mono font-bold px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-500 border border-amber-500/30 shrink-0 flex items-center gap-0.5">
                                <Lock className="w-2.5 h-2.5" /> PRO
                              </span>
                            )}
                          </div>
                          <p className={`text-[11px] line-clamp-2 mt-0.5 leading-relaxed ${isLight ? 'text-slate-600' : 'text-[#94A3B8]'}`}>
                            {item.message}
                          </p>
                          {item.is_locked && (
                            <p className="text-[10px] text-emerald-500 font-semibold mt-1 flex items-center gap-1">
                              Unlock Drishti Pro to access trade levels <ArrowRight className="w-2.5 h-2.5" />
                            </p>
                          )}
                          {item.created_at && (
                            <p className={`text-[10px] font-mono mt-1 ${isLight ? 'text-slate-400' : 'text-[#52525B]'}`}>
                              {new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          )}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={`flex items-center gap-2 ${isLight ? 'hover:bg-slate-100' : 'hover:bg-white/[0.03]'}`} data-testid="header-user-menu-btn">
                <Avatar className="w-8 h-8 border border-white/10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className={`text-xs font-outfit ${isLight ? 'bg-slate-100 text-slate-800' : 'bg-white/5 text-white'}`}>{user?.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                <span className={`hidden md:block text-sm font-medium ${isLight ? 'text-slate-800' : 'text-white'}`}>{user?.name?.split(" ")[0]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={`w-48 border ${isLight ? 'bg-white border-slate-200 text-slate-900 shadow-lg' : 'bg-[#0C0C10] border-white/[0.06] text-white'}`}>
              <div className={`px-3 py-2 border-b ${isLight ? 'border-slate-100' : 'border-white/[0.06]'}`}>
                <p className={`text-sm font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}>{user?.name}</p>
                <p className={`text-xs truncate ${isLight ? 'text-slate-500' : 'text-[#52525B]'}`}>{user?.email}</p>
              </div>
              <DropdownMenuItem className={`cursor-pointer ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} onClick={() => navigate("/profile")} data-testid="user-menu-profile">Profile</DropdownMenuItem>
              <DropdownMenuItem className={`cursor-pointer ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} onClick={() => navigate("/payments")} data-testid="user-menu-payments">Payment History</DropdownMenuItem>
              <DropdownMenuSeparator className={isLight ? 'bg-slate-100' : 'bg-white/[0.06]'} />
              <DropdownMenuItem asChild className="hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 cursor-pointer" data-testid="user-menu-logout">
                <a href={logoutUrl}>Logout</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Global Interactive Command Palette / Search Dialog (M1) */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className={`max-w-xl p-0 overflow-hidden border shadow-2xl ${
          isLight ? 'bg-white border-slate-200 text-slate-900' : 'bg-[#0E131F] border-white/10 text-white'
        }`}>
          <DialogHeader className="sr-only">
            <DialogTitle>Quick Search & Navigation</DialogTitle>
          </DialogHeader>

          {/* Search Header Input */}
          <div className="relative border-b border-gray-200 dark:border-white/10 p-3 flex items-center">
            <Search className="w-5 h-5 text-gray-400 ml-2 shrink-0" />
            <input
              type="text"
              autoFocus
              placeholder="Search courses, signals, risk calculator, tools, admin..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent pl-3 pr-8 py-2 text-sm outline-none font-plex placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="text-gray-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Search Results List */}
          <div className="max-h-80 overflow-y-auto p-2 space-y-1 custom-scrollbar">
            {filteredSearchResults.length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-400 font-plex">
                No matching pages or tools found for "{searchQuery}".
              </div>
            ) : (
              filteredSearchResults.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelectResult(item.path)}
                    className={`w-full text-left p-3 rounded-xl flex items-center justify-between gap-3 transition-colors ${
                      isLight ? 'hover:bg-slate-100' : 'hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        item.category === "Admin"
                          ? 'bg-amber-500/10 text-amber-500'
                          : 'bg-emerald-500/10 text-emerald-500'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold truncate font-outfit">{item.title}</p>
                          <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded ${
                            item.category === "Admin"
                              ? 'bg-amber-500/10 text-amber-500'
                              : 'bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-gray-400'
                          }`}>
                            {item.category}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-plex mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                );
              })
            )}
          </div>

          {/* Search Footer Tip */}
          <div className={`px-4 py-2 text-[11px] border-t flex items-center justify-between font-mono ${
            isLight ? 'bg-slate-50 border-slate-200 text-slate-500' : 'bg-black/30 border-white/[0.06] text-gray-500'
          }`}>
            <span>Navigation shortcut</span>
            <span>ESC to close</span>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
